// src/components/job-engine/signalRService.ts
import type { HubConnection } from "@microsoft/signalr";
import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import type { JobEngineEventName } from "@/api/jobEngine";

class JobEngineSignalRService {
  private connection: HubConnection | null = null;
  private isConnecting = false;
  private stateSubscribers = new Set<(connected: boolean) => void>();

  onStateChange(cb: (connected: boolean) => void): void {
    this.stateSubscribers.add(cb);
  }

  offStateChange(cb: (connected: boolean) => void): void {
    this.stateSubscribers.delete(cb);
  }

  private notifyStateChange(connected: boolean): void {
    this.stateSubscribers.forEach((cb) => { try { cb(connected); } catch { /* swallow */ } });
  }

  private eventCallbacks = new Map<string, Set<(data: unknown) => void>>();

  async startConnection(apiBaseUrl: string, accessToken: string, customerId: string): Promise<void> {
    if (this.connection || this.isConnecting) return;

    if (!accessToken || accessToken.trim() === "") {
      throw new Error("Cannot connect to SignalR without a valid authentication token");
    }

    this.isConnecting = true;
    try {
      const baseUrl = apiBaseUrl.replace(/\/$/, "");
      const hubUrl = `${baseUrl}/v1/hubs/jobs`;
      const headers: Record<string, string> = customerId ? { "Customer-Id": customerId } : {};

      // transport: LongPolling — pinned (matches Pcs.Opportunity.UI's working
      // pattern). The /v1/hubs/jobs path is reached through APIM
      // (https://{env}-api.pcssoft.com/jobengine/...), which doesn't reliably
      // proxy WebSocket upgrades; SignalR's auto-fallback gets confused by
      // intermediate gateways and ends up silently failing. LongPolling is
      // pure HTTP and APIM proxies it cleanly.
      const builder = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          transport: HttpTransportType.LongPolling,
          headers,
          accessTokenFactory: () => accessToken,
        })
        .configureLogging(LogLevel.Information);

      this.connection = builder.build();
      this.setupEventHandlers();
      await this.connection.start();
      // eslint-disable-next-line no-console
      console.log("[JobEngineSignalR] Connected", {
        connectionId: this.connection.connectionId,
        hubUrl,
      });
      this.notifyStateChange(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[JobEngineSignalR] Connection failed", err);
      this.connection = null;
      this.notifyStateChange(false);
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  async stopConnection(): Promise<void> {
    if (!this.connection) return;
    try {
      await this.connection.stop();
    } finally {
      this.connection = null;
      this.notifyStateChange(false);
    }
  }

  on(eventName: JobEngineEventName, callback: (data: unknown) => void): void {
    if (!this.eventCallbacks.has(eventName)) {
      this.eventCallbacks.set(eventName, new Set());
    }
    this.eventCallbacks.get(eventName)!.add(callback);
  }

  off(eventName: JobEngineEventName, callback: (data: unknown) => void): void {
    this.eventCallbacks.get(eventName)?.delete(callback);
  }

  isConnected(): boolean {
    return this.connection?.state === "Connected";
  }

  getConnectionId(): string | null {
    return this.connection?.connectionId ?? null;
  }

  private emit(eventName: string, data: unknown): void {
    this.eventCallbacks.get(eventName)?.forEach((cb) => cb(data));
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;
    const eventNames: JobEngineEventName[] = [
      "JobStarted",
      "JobFinished",
      "JobFailed",
      "JobDeadLettered",
    ];
    for (const name of eventNames) {
      this.connection.on(name, (notification: unknown) => {
        // eslint-disable-next-line no-console
        console.log("[JobEngineSignalR] Event received", { name, notification });
        this.emit(name, notification);
      });
    }
    this.connection.onclose((error) => {
      // eslint-disable-next-line no-console
      console.log("[JobEngineSignalR] Connection closed", { error: error || "none" });
      this.connection = null;
      this.notifyStateChange(false);
    });
  }
}

export const signalRService = new JobEngineSignalRService();
export default signalRService;
